// Use relative URL in production to avoid ad blocker issues
// The _redirects file will proxy to the worker
const API_BASE_URL = import.meta.env.DEV
  ? 'https://navigation-gr-api.telenavis-com-account.workers.dev/api'
  : '/api';

console.log('API Configuration:', {
  isDev: import.meta.env.DEV,
  API_BASE_URL: API_BASE_URL,
  mode: import.meta.env.MODE
});

export const api = {
  // POIs
  getPOIs: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_BASE_URL}/pois?${query}`);
    if (!response.ok) throw new Error('Failed to fetch POIs');
    return response.json();
  },

  getPOI: async (id) => {
    const response = await fetch(`${API_BASE_URL}/pois/${id}`);
    if (!response.ok) throw new Error('Failed to fetch POI');
    return response.json();
  },

  getPOICategories: async () => {
    const response = await fetch(`${API_BASE_URL}/pois/meta/categories`);
    if (!response.ok) throw new Error('Failed to fetch categories');
    return response.json();
  },

  getPOIsWithinPolygon: async (polygon) => {
    const response = await fetch(`${API_BASE_URL}/pois/within-polygon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ polygon }),
    });
    if (!response.ok) throw new Error('Failed to find POIs within polygon');
    return response.json();
  },

  aiSearchPOIs: async (query) => {
    const response = await fetch(`${API_BASE_URL}/pois/ai-search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (!response.ok) throw new Error('AI search failed');
    return response.json();
  },

  // Events
  getEvents: async (params = {}) => {
    // Add cache-busting timestamp to force fresh data
    const cacheBuster = { _t: Date.now(), ...params };
    const query = new URLSearchParams(cacheBuster).toString();
    const response = await fetch(`${API_BASE_URL}/events?${query}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    if (!response.ok) throw new Error('Failed to fetch events');
    return response.json();
  },

  // Get all events including inactive (for admin)
  getAllEvents: async (params = {}) => {
    // Add cache-busting timestamp
    const cacheBuster = { _t: Date.now(), ...params, includeInactive: 'true' };
    const query = new URLSearchParams(cacheBuster).toString();
    const response = await fetch(`${API_BASE_URL}/events?${query}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    if (!response.ok) throw new Error('Failed to fetch events');
    return response.json();
  },

  getEvent: async (identifier) => {
    const url = `${API_BASE_URL}/events/${identifier}`;
    console.log('Fetching event from:', url);
    try {
      const response = await fetch(url);
      console.log('Response status:', response.status);
      if (!response.ok) {
        console.error('Failed to fetch event, status:', response.status);
        throw new Error('Failed to fetch event');
      }
      const data = await response.json();
      console.log('Event data received:', data);
      return data;
    } catch (error) {
      console.error('Error fetching event:', error, 'URL:', url);
      throw error;
    }
  },

  // Routing
  calculateRoute: async (data) => {
    const response = await fetch(`${API_BASE_URL}/route`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to calculate route');
    return response.json();
  },

  // Geocoding
  geocodeSearch: async (q, limit = 5) => {
    const params = new URLSearchParams({ q, limit });
    const response = await fetch(`${API_BASE_URL}/geocode/search?${params}`);
    if (!response.ok) throw new Error('Geocoding failed');
    return response.json();
  },

  reverseGeocode: async (lat, lng) => {
    const params = new URLSearchParams({ lat, lng });
    const response = await fetch(`${API_BASE_URL}/geocode/reverse?${params}`);
    if (!response.ok) throw new Error('Reverse geocoding failed');
    return response.json();
  },

  // Admin
  adminLogin: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) throw new Error('Login failed');
    return response.json();
  },

  adminVerify: async (token) => {
    const response = await fetch(`${API_BASE_URL}/admin/verify`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Token verification failed');
    return response.json();
  },

  adminCreateEvent: async (token, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create event');
    return response.json();
  },

  adminUpdateEvent: async (token, id, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/events/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update event');
    return response.json();
  },

  adminDeleteEvent: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/admin/events/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to delete event');
    return response.json();
  },

  // Event Layer Management
  adminCreateEventLayer: async (token, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/events/layers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create event layer');
    return response.json();
  },

  adminUpdateEventLayer: async (token, id, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/events/layers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update event layer');
    return response.json();
  },

  adminDeleteEventLayer: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/admin/events/layers/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to delete event layer');
    return response.json();
  },

  adminCreatePOI: async (token, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/pois`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create POI');
    return response.json();
  },

  adminUpdatePOI: async (token, id, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/pois/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update POI');
    return response.json();
  },

  adminDeletePOI: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/admin/pois/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to delete POI');
    return response.json();
  },
};

export default api;
